import { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import Chip from "@/components/atoms/Chip";
import Input from "@/components/atoms/input/Input";

type KeywordsInputProps = {
  name: string;
  placeholder: string;
};

const FormWordSplitter = ({ name, placeholder }: KeywordsInputProps) => {
  const { control, setValue } = useFormContext();
  const keywords: string[] = useWatch({ control, name }) || [];
  const [inputValue, setInputValue] = useState("");

  const commitInput = useCallback(
    (value: string) => {
      const parts = value
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part !== "");
      if (parts.length > 0) {
        setValue(name, [...keywords, ...parts]);
      }
    },
    [keywords, name, setValue],
  );

  const handleBlur = useCallback(() => {
    if (inputValue.trim() !== "") {
      commitInput(inputValue.trim());
      setInputValue("");
    }
  }, [commitInput, inputValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (inputValue.trim() !== "") {
          commitInput(inputValue.trim());
          setInputValue("");
        }
      }
    },
    [commitInput, inputValue],
  );

  return (
    <div>
      <Input
        type="text"
        value={inputValue}
        placeholder={placeholder}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {keywords.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {keywords.map((keyword, index) => (
            <Chip
              key={index}
              label={keyword}
              size="xs"
              color="natural"
              className="max-w-28"
              onRemove={() =>
                setValue(
                  name,
                  keywords.filter((_, i) => i !== index),
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FormWordSplitter;
